import React, { useEffect } from 'react';
import { Helmet } from "react-helmet-async";
import { CheckCircle, GraduationCap, Sparkles, FileCheck, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function DoctoralContent() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="py-5" style={{ background: '#f8fafc', minHeight: '85vh' }}>
      <Helmet>
        <title>Doctor of Theology (D.Th.) - Vidyajyoti College of Theology</title>
        <meta name="description" content="Doctor of Theology (D.Th.) - Third Cycle Ecclesiastical Research Degree at Vidyajyoti College of Theology, Delhi." />
        <meta name="keywords" content="Doctor of Theology, DTh Delhi, PhD Theology India, Ecclesiastical Doctorate, Vidyajyoti research" />
        <link rel="canonical" href="/academics/doctoral" />
      </Helmet>

      <div className="container">
        {/* Hero Card */}
        <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 mb-5" style={{ background: 'linear-gradient(135deg, #1a2b56 0%, #0d1b3e 100%)', color: '#fff' }}>
          <div className="row align-items-center">
            <div className="col-lg-8">
              <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3" style={{ background: 'rgba(198, 146, 20, 0.2)', color: '#ffd56b', fontSize: '0.85rem', fontWeight: 600, border: '1px solid rgba(198, 146, 20, 0.4)' }}>
                <Sparkles size={14} /> Third Cycle (Terminal Research Degree)
              </div>
              <h1 className="display-6 fw-bold mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Doctor of Theology (D.Th.)
              </h1>
              <h5 className="text-light fw-normal mb-3" style={{ opacity: 0.9 }}>
                Doctoratus in Sacra Theologia
              </h5>
              <p className="lead mb-0 text-light" style={{ fontSize: '1rem', lineHeight: '1.7', opacity: 0.85 }}>
                The third cycle of ecclesiastical studies applies scientific research methods to theological exploration, culminating in a definitive doctoral dissertation that contributes originally to theological science.
              </p>
            </div>

            <div className="col-lg-4 text-center mt-4 mt-lg-0">
              <div className="p-4 rounded-4" style={{ background: 'rgba(255, 255, 255, 0.08)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.15)' }}>
                <GraduationCap size={44} className="mb-2" style={{ color: '#ffd56b' }} />
                <h5 className="fw-bold mb-1 text-white">Ecclesiastical Doctorate</h5>
                <p className="small mb-0 text-light opacity-75">Pontifical Degree Recognized Globally</p>
                <hr style={{ borderColor: 'rgba(255,255,255,0.2)' }} />
                <Link to="/contact" className="btn btn-sm w-100 rounded-pill fw-semibold text-white" style={{ background: '#79031d' }}>
                  Research Inquiries
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Evaluation & Structure Breakdown */}
        <div className="row g-4 mb-5">
          <div className="col-md-6">
            <div className="card h-100 border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white">
              <div className="d-flex align-items-center gap-3 mb-4">
                <div className="p-3 rounded-circle" style={{ background: 'rgba(26, 43, 86, 0.08)', color: '#1a2b56' }}>
                  <FileCheck size={28} />
                </div>
                <div>
                  <h4 className="fw-bold mb-0" style={{ color: '#1a2b56' }}>Doctoral Thesis Evaluation</h4>
                  <small className="text-muted">Rigorous Scientific Scrutiny</small>
                </div>
              </div>

              <div className="mb-4">
                <div className="d-flex justify-content-between mb-1">
                  <span className="fw-semibold text-dark">Written Dissertation Evaluation</span>
                  <span className="fw-bold text-primary">80% Weightage</span>
                </div>
                <div className="progress" style={{ height: '8px', borderRadius: '4px' }}>
                  <div className="progress-bar" style={{ width: '80%', background: '#1a2b56' }}></div>
                </div>
                <small className="text-muted mt-1 d-block">Evaluated independently by a board of three expert doctoral examiners.</small>
              </div>

              <div>
                <div className="d-flex justify-content-between mb-1">
                  <span className="fw-semibold text-dark">Public Academic Defence</span>
                  <span className="fw-bold" style={{ color: '#79031d' }}>20% Weightage</span>
                </div>
                <div className="progress" style={{ height: '8px', borderRadius: '4px' }}>
                  <div className="progress-bar" style={{ width: '20%', background: '#79031d' }}></div>
                </div>
                <small className="text-muted mt-1 d-block">Public viva voce defense before the faculty and academic peers.</small>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card h-100 border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white">
              <div className="d-flex align-items-center gap-3 mb-4">
                <div className="p-3 rounded-circle" style={{ background: 'rgba(198, 146, 20, 0.12)', color: '#c69214' }}>
                  <Layers size={28} />
                </div>
                <div>
                  <h4 className="fw-bold mb-0" style={{ color: '#1a2b56' }}>Doctoral Programme Scope</h4>
                  <small className="text-muted">Research Methodology &amp; Seminars</small>
                </div>
              </div>

              <ul className="list-unstyled d-flex flex-column gap-3 mb-0">
                <li className="d-flex align-items-start gap-2">
                  <CheckCircle size={18} className="text-success flex-shrink-0 mt-1" />
                  <span className="text-muted small">Specialized doctoral seminars, research workshops, and colloquia.</span>
                </li>
                <li className="d-flex align-items-start gap-2">
                  <CheckCircle size={18} className="text-success flex-shrink-0 mt-1" />
                  <span className="text-muted small">Focus on contextual theologizing, Asian religiosity, subaltern hermeneutics, and socio-ethical challenges.</span>
                </li>
                <li className="d-flex align-items-start gap-2">
                  <CheckCircle size={18} className="text-success flex-shrink-0 mt-1" />
                  <span className="text-muted small">Access to the extensive 1,34,000+ volume Vidyajyoti Theological Research Library.</span>
                </li>
                <li className="d-flex align-items-start gap-2">
                  <CheckCircle size={18} className="text-success flex-shrink-0 mt-1" />
                  <span className="text-muted small">Prerequisite: Pontifical Licentiate in Sacred Theology (S.T.L.) or equivalent research master&rsquo;s with high distinction.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
